import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, Paper, CircularProgress, Chip, Divider,
  Button, Dialog, DialogTitle, DialogContent, DialogActions,
  Table, TableHead, TableRow, TableCell, TableBody,
  Tooltip,
} from '@mui/material';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip as RTooltip, ResponsiveContainer, ReferenceLine,
} from 'recharts';
import HistoryIcon from '@mui/icons-material/History';
import SportsMotorsportsIcon from '@mui/icons-material/SportsMotorsports';
import BuildIcon from '@mui/icons-material/Build';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { getRecentSessions, lapTimeToSeconds } from '../../utils/reSessionsService';
import { useTranslation } from 'react-i18next';
import { trackNameToSlug } from '../../utils/reTracksService';

// ── Formatta delta secondi ────────────────────────────────────────
function DeltaBadge({ delta }) {
  if (delta == null) return null;
  const color = delta < 0 ? 'success' : 'error';
  return (
    <Chip size="small" color={color} variant="outlined"
      label={`${delta > 0 ? '+' : ''}${delta.toFixed(3)}s`}
      sx={{ fontSize: 10, height: 18 }} />
  );
}

// ── Heatmap settori per sessione ──────────────────────────────────
function SectorHeatmap({ sessions }) {
  if (!sessions.length) return null;
  const maxSectors = Math.max(...sessions.map(s => s.sectors?.length || 0));
  if (maxSectors === 0) return null;

  const getColor = (delta) => {
    if (delta == null) return '#333';
    if (delta <= 0.02) return '#2e7d32';
    if (delta <= 0.1)  return '#f57f17';
    return '#c62828';
  };

  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: `80px repeat(${maxSectors}, 1fr)`, gap: 0.25, minWidth: 400 }}>
        {/* Header */}
        <Box />
        {Array.from({ length: maxSectors }, (_, i) => (
          <Typography key={i} variant="caption" color="text.disabled"
            sx={{ textAlign: 'center', fontSize: 9, fontWeight: 700 }}>S{i+1}</Typography>
        ))}
        {/* Rows */}
        {sessions.slice(0, 8).map(s => (
          <React.Fragment key={s.id}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 9, alignSelf: 'center', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {s.date}
            </Typography>
            {Array.from({ length: maxSectors }, (_, i) => {
              const sec  = s.sectors?.[i];
              const delta = sec?.delta_vs_ref_s ?? sec?.delta_vs_best_s;
              return (
                <Tooltip key={i} title={delta != null ? `S${i+1}: ${delta > 0 ? '+' : ''}${delta.toFixed(3)}s` : 'N/D'}>
                  <Box sx={{ height: 16, borderRadius: 0.5, bgcolor: getColor(delta), opacity: 0.85 }} />
                </Tooltip>
              );
            })}
          </React.Fragment>
        ))}
      </Box>
      <Box sx={{ display: 'flex', gap: 1.5, mt: 1 }}>
        {[['Perfetto', '#2e7d32'], ['Margine', '#f57f17'], ['Critico', '#c62828']].map(([l, c]) => (
          <Box key={l} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: c }} />
            <Typography variant="caption" color="text.disabled" sx={{ fontSize: 9 }}>{l}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

// ── Componente principale ─────────────────────────────────────────
export default function SessionHistory({ open, onClose, teamId, trackSlug, trackName }) {
  const { t } = useTranslation('race_engineer');
  const [sessions, setSessions]   = useState([]);
  const [loading, setLoading]     = useState(false);

  useEffect(() => {
    if (!open || !teamId || !trackSlug) return;
    setLoading(true);
    getRecentSessions({ teamId, trackSlug, n: 20 })
      .then(setSessions)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [open, teamId, trackSlug]);

  // Dati per grafico trend
  const chartData = useMemo(() => sessions
    .filter(s => s.lapTime_s)
    .map(s => ({ date: s.date, t: s.lapTime_s, label: s.lapTimeStr }))
    .reverse(), [sessions]);

  const best = chartData.length ? Math.min(...chartData.map(d => d.t)) : null;

  // Problemi ricorrenti
  const problemMap = {};
  sessions.forEach(s => (s.findings || []).forEach(f => {
    if (!f.curva) return;
    problemMap[f.curva] = (problemMap[f.curva] || 0) + 1;
  }));
  const topProblems = Object.entries(problemMap)
    .sort((a, b) => b[1] - a[1]).slice(0, 5);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth
      PaperProps={{ sx: { minHeight: '70vh' } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, fontWeight: 700 }}>
        <HistoryIcon sx={{ color: 'secondary.main' }} />
        {t('history.title')} — {trackName || trackSlug}
      </DialogTitle>

      <DialogContent>
        {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>}

        {!loading && sessions.length === 0 && (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <Typography color="text.secondary">{t('history.no_sessions')}</Typography>
            <Typography variant="caption" color="text.disabled">
              {t('history.sessions_hint')}
            </Typography>
          </Box>
        )}

        {!loading && sessions.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

            {/* ── Trend Tempo Giro ── */}
            {chartData.length >= 2 && (
              <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', p: 2 }}>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                  {t('history.lap_trend')}
                </Typography>
                <ResponsiveContainer width="100%" height={160}>
                  <LineChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="2 6" stroke="rgba(255,255,255,0.05)" />
                    <XAxis dataKey="date" tick={{ fontSize: 10, fill: 'rgba(255,255,255,0.4)' }} />
                    <YAxis
                      domain={['auto', 'auto']}
                      tick={{ fontSize: 10, fill: 'rgba(255,255,255,0.4)' }}
                      tickFormatter={v => { const m = Math.floor(v/60); return `${m}:${(v%60).toFixed(2).padStart(5,'0')}`; }}
                      width={52}
                    />
                    <RTooltip
                      formatter={(v) => { const m = Math.floor(v/60); return [`${m}:${(v%60).toFixed(3).padStart(6,'0')}`, 'Tempo']; }}
                      contentStyle={{ background: '#1a1a1a', border: '1px solid #333', fontSize: 12 }}
                    />
                    {best && <ReferenceLine y={best} stroke="rgba(0,200,80,0.4)" strokeDasharray="4 2" label={{ value: 'Best', fill: 'rgba(0,200,80,0.7)', fontSize: 9 }} />}
                    <Line type="monotone" dataKey="t" stroke="#4FC3F7" strokeWidth={2}
                      dot={{ r: 4, fill: '#4FC3F7' }} activeDot={{ r: 6 }} isAnimationActive={false} />
                  </LineChart>
                </ResponsiveContainer>
              </Paper>
            )}

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>

              {/* ── Heatmap settori ── */}
              <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', p: 2 }}>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                  {t('history.sector_delta')} ({t('history.last_sessions', { count: Math.min(sessions.length, 8) })})
                </Typography>
                <SectorHeatmap sessions={sessions} />
              </Paper>

              {/* ── Problemi ricorrenti ── */}
              <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', p: 2 }}>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                  {t('history.recurring_areas')}
                </Typography>
                {topProblems.length === 0
                  ? <Typography variant="caption" color="text.disabled">{t('history.no_pattern')}</Typography>
                  : topProblems.map(([area, count]) => (
                    <Box key={area} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5 }}>
                      <Typography variant="body2" fontWeight={count >= 3 ? 700 : 400}>{area}</Typography>
                      <Chip size="small" label={`×${count}`}
                        color={count >= 4 ? 'error' : count >= 3 ? 'warning' : 'default'}
                        sx={{ fontSize: 10, height: 18 }} />
                    </Box>
                  ))
                }
              </Paper>
            </Box>

            {/* ── Tabella sessioni ── */}
            <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ px: 2, pt: 1.5, pb: 1 }}>
                {t('history.sessions_title')} ({sessions.length})
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ '& th': { fontSize: 11, fontWeight: 700, color: 'text.secondary' } }}>
                    <TableCell>{t('history.date')}</TableCell>
                    <TableCell>{t('history.driver')}</TableCell>
                    <TableCell align="right">Tempo</TableCell>
                    <TableCell align="right">Rif.</TableCell>
                    <TableCell>Tipo</TableCell>
                    <TableCell>Setup</TableCell>
                    <TableCell>{t('history.summary')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sessions.map(s => (
                    <TableRow key={s.id} hover sx={{ '& td': { fontSize: 11 } }}>
                      <TableCell>{s.date}</TableCell>
                      <TableCell>{s.driverName}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, fontFamily: 'monospace' }}>{s.lapTimeStr}</TableCell>
                      <TableCell align="right" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>{s.refLapTimeStr || '—'}</TableCell>
                      <TableCell>
                        {s.coachingType === 'guida'
                          ? <Chip icon={<SportsMotorsportsIcon sx={{ fontSize: '12px !important' }} />} label="Guida" size="small" color="primary" variant="outlined" sx={{ fontSize: 10, height: 18 }} />
                          : s.coachingType === 'setup'
                          ? <Chip icon={<BuildIcon sx={{ fontSize: '12px !important' }} />} label="Setup" size="small" color="secondary" variant="outlined" sx={{ fontSize: 10, height: 18 }} />
                          : <Typography variant="caption" color="text.disabled">—</Typography>
                        }
                      </TableCell>
                      <TableCell sx={{ color: 'text.secondary' }}>
                        {[s.setupA?.sessionName, s.setupB?.sessionName].filter(Boolean).join(' / ') || '—'}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 240 }}>
                        <Typography variant="caption" color="text.secondary"
                          sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                          {s.summary || '—'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>

          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>{t('history.close')}</Button>
      </DialogActions>
    </Dialog>
  );
}
